import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const FIND_RETRY_MS = 250;
const FIND_MAX_ATTEMPTS = 12; // ~3s
const SETTLE_MS = 3500; // keep correcting while lazy content above shifts layout
const SETTLE_TICK_MS = 200;

function targetOffset(el) {
  return parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
}

/**
 * Scrolls to `location.hash` on mount / hash change. Keeps re-aligning for a
 * few seconds afterwards because sections above (clips, YouTube, stats) load
 * async and push the target down after the first scroll. Stops as soon as the
 * user scrolls themselves.
 */
export function useHashScroll() {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    const id = hash.slice(1);
    if (!id) return undefined;

    let cancelled = false;
    let attempts = 0;
    let settleTimer = null;

    const stop = () => {
      cancelled = true;
      if (settleTimer) clearInterval(settleTimer);
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('keydown', stop);
    };

    const keepAligned = (el) => {
      const started = performance.now();
      settleTimer = setInterval(() => {
        if (cancelled || performance.now() - started > SETTLE_MS) {
          clearInterval(settleTimer);
          return;
        }
        const drift = el.getBoundingClientRect().top - targetOffset(el);
        if (Math.abs(drift) > 4) window.scrollBy({ top: drift, behavior: 'auto' });
      }, SETTLE_TICK_MS);
    };

    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => !cancelled && keepAligned(el), 600);
        window.addEventListener('wheel', stop, { passive: true });
        window.addEventListener('touchstart', stop, { passive: true });
        window.addEventListener('keydown', stop);
        return;
      }
      attempts += 1;
      if (attempts < FIND_MAX_ATTEMPTS) setTimeout(tryScroll, FIND_RETRY_MS);
    };

    tryScroll();
    return stop;
  }, [hash, pathname]);
}
